import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useMySites, useUoms, useWorkPackages } from '../api/hooks'
import {
  useCreateWorkItem,
  useDeleteWorkItem,
  useUpdateWorkItem,
  useWorkItems,
  type WorkItem,
} from '../api/workItems'
import { useAuth } from '../auth/useAuth'
import { CrudTable, type CrudField, type FormValues } from '../components/CrudTable'
import { PageHeader } from '../components/PageHeader'

const STATUSES = [
  { value: 'PLANNED', label: 'Planned' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PENDING', label: 'Pending' },
]

/**
 * The site's bill of quantities.
 *
 * <p>The first thing to set up after creating a site, and until now the one thing that
 * could not be: work completion is recorded against these lines, so a site without them
 * has an entry screen that cannot be used.</p>
 */
export function WorkItemsPage() {
  const { can } = useAuth()
  const sites = useMySites()
  const [siteId, setSiteId] = useState('')
  const chosenSite = siteId || sites.data?.[0]?.id || ''

  const items = useWorkItems(chosenSite || undefined)
  const packages = useWorkPackages()
  const uoms = useUoms()

  const create = useCreateWorkItem(chosenSite)
  const update = useUpdateWorkItem(chosenSite)
  const remove = useDeleteWorkItem(chosenSite)

  const canWrite = can('WORK_ITEM:CREATE')
  const canUpdate = can('WORK_ITEM:UPDATE')
  const canDelete = can('WORK_ITEM:DELETE')

  const fields: CrudField[] = [
    {
      name: 'code',
      label: 'BOQ code',
      required: true,
      lockedOnEdit: false,
      helperText: 'e.g. STR-01, or the code from the priced bill',
    },
    { name: 'description', label: 'Description', required: true, full: true },
    {
      name: 'workPackageId',
      label: 'Work package',
      type: 'select',
      required: true,
      options: (packages.data ?? []).map((p) => ({ value: p.id, label: p.name })),
    },
    {
      name: 'uomId',
      label: 'Unit',
      type: 'select',
      required: true,
      options: (uoms.data ?? []).map((u) => ({ value: u.id, label: `${u.code} — ${u.name}` })),
    },
    {
      name: 'totalPlannedQuantity',
      label: 'Planned quantity',
      type: 'number',
      required: true,
      helperText: 'The total to be built. Every percentage divides by this.',
    },
    {
      name: 'weightage',
      label: 'Weightage',
      type: 'number',
      defaultValue: 1,
      helperText: 'How much this line counts toward overall progress',
    },
    { name: 'plannedStartDate', label: 'Planned start', type: 'date' },
    { name: 'plannedFinishDate', label: 'Planned finish', type: 'date' },
    { name: 'status', label: 'Status', type: 'select', defaultValue: 'PLANNED', options: STATUSES },
    { name: 'remarks', label: 'Remarks', type: 'multiline' },
  ]

  function body(values: FormValues) {
    return {
      code: String(values.code ?? '').trim(),
      description: String(values.description ?? '').trim(),
      workPackageId: String(values.workPackageId ?? ''),
      uomId: String(values.uomId ?? ''),
      totalPlannedQuantity: Number(values.totalPlannedQuantity ?? 0),
      weightage: values.weightage === '' ? null : Number(values.weightage),
      plannedStartDate: values.plannedStartDate ? String(values.plannedStartDate) : null,
      plannedFinishDate: values.plannedFinishDate ? String(values.plannedFinishDate) : null,
      status: values.status ? (String(values.status) as WorkItem['status']) : undefined,
      remarks: values.remarks ? String(values.remarks) : undefined,
    }
  }

  const rows = items.data ?? []
  const noPackages = (packages.data?.length ?? 0) === 0
  const noUoms = (uoms.data?.length ?? 0) === 0

  return (
    <>
      <PageHeader
        title="Site BOQ"
        subtitle="What there is to build on this site, and how much of it. Daily work is recorded against these lines."
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Site"
          value={chosenSite}
          onChange={(e) => setSiteId(e.target.value)}
          sx={{ minWidth: 300 }}
        >
          {(sites.data ?? []).map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.code} - {s.name}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {(noPackages || noUoms) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Set up the masters first</AlertTitle>
          A BOQ line needs a work package and a unit.{' '}
          {noPackages && 'No work packages exist yet. '}
          {noUoms && 'No units of measure exist yet. '}
          Add them under Masters → Reference data.
        </Alert>
      )}

      {chosenSite && rows.length === 0 && !items.isLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This site has no BOQ lines, which is why its work completion screen has nothing to
          record against. Add the lines here and they appear there immediately.
        </Alert>
      )}

      <Paper variant="outlined">
        <CrudTable
          query={items}
          rows={rows}
          addLabel="Add BOQ line"
          describeRow={(item) => `${item.code} — ${item.description}`}
          emptyMessage="No BOQ lines on this site yet."
          fields={fields}
          columns={[
            { label: 'Code', render: (i) => <span style={{ fontFamily: 'monospace' }}>{i.code}</span> },
            { label: 'Description', render: (i) => i.description },
            { label: 'Package', render: (i) => i.workPackageName ?? '-' },
            { label: 'Unit', render: (i) => i.uomCode ?? '-' },
            { label: 'Planned', render: (i) => fmt(i.totalPlannedQuantity), align: 'right' },
            { label: 'Done', render: (i) => fmt(i.completedQuantity), align: 'right' },
            { label: 'Balance', render: (i) => fmt(i.balanceQuantity), align: 'right' },
            {
              label: 'Progress',
              render: (i) => (
                <Box sx={{ minWidth: 110, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(i.percentComplete ?? 0, 100)}
                    sx={{ flex: 1, height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption">{i.percentComplete ?? 0}%</Typography>
                </Box>
              ),
            },
            {
              label: 'Status',
              render: (i) =>
                i.delayed ? (
                  <Chip size="small" label="delayed" sx={{ bgcolor: '#fbe0de', color: '#8c1d18', fontWeight: 700 }} />
                ) : (
                  <Chip size="small" label={i.status.replace('_', ' ').toLowerCase()} sx={{ textTransform: 'capitalize' }} />
                ),
            },
          ]}
          toFormValues={(item) => ({ ...item })}
          onCreate={canWrite && !noPackages && !noUoms ? (values) => create.mutateAsync(body(values)) : undefined}
          onUpdate={canUpdate ? (row, values) => update.mutateAsync({ id: row.id, body: body(values) }) : undefined}
          onDelete={canDelete ? (row) => remove.mutateAsync(row.id) : undefined}
          deleteNote="A line with work already recorded against it cannot be removed — the server refuses it, because those quantities are signed-off history. Put it on hold instead."
        />
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        The planned quantity is the denominator of every percentage this system reports, and
        it cannot be reduced below what has already been recorded as complete.
      </Typography>
    </>
  )
}

function fmt(value?: number): string {
  if (value == null) return '-'
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 3 })
}
